import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../../../helpers/apiRequester'
import { BatchItemRow, BatchRow } from '../types'

const LIST_POLL_MS = 4000
const DETAIL_POLL_MS = 2500

/**
 * Loads the batch list and keeps polling while any batch is running.
 * Callers can also force a refresh (after creating/starting a batch).
 */
export const useBatchList = (enabled: boolean) => {
    const [batches, setBatches] = useState<BatchRow[]>([])
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const inFlight = useRef(false)

    const refresh = useCallback(async () => {
        if (inFlight.current) return
        inFlight.current = true
        try {
            const response = await api({ url: 'contract-sign/bulk/list', data: {} })
            if (response.data?.success) {
                setBatches(response.data.batches || [])
                setError(null)
            } else {
                setError(response.data?.message || 'Could not load bulk sends.')
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Could not load bulk sends.')
        } finally {
            inFlight.current = false
            setLoaded(true)
        }
    }, [])

    useEffect(() => {
        if (!enabled) return
        refresh()
    }, [enabled, refresh])

    const anyRunning = batches.some((b) => b.status === 'running')
    useEffect(() => {
        if (!enabled || !anyRunning) return
        const id = window.setInterval(refresh, LIST_POLL_MS)
        return () => window.clearInterval(id)
    }, [enabled, anyRunning, refresh])

    return { batches, loaded, error, refresh }
}

export interface BatchDetailState {
    batch: BatchRow | null
    items: BatchItemRow[]
    loaded: boolean
    error: string | null
    refresh: () => Promise<void>
}

/** Loads one batch with its items; polls while the batch is running. */
export const useBatchDetail = (batchId: number | null): BatchDetailState => {
    const [batch, setBatch] = useState<BatchRow | null>(null)
    const [items, setItems] = useState<BatchItemRow[]>([])
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const inFlight = useRef(false)

    const refresh = useCallback(async () => {
        if (!batchId || inFlight.current) return
        inFlight.current = true
        try {
            const response = await api({ url: 'contract-sign/bulk/get', data: { batch_id: batchId } })
            if (response.data?.success) {
                setBatch(response.data.batch)
                setItems(response.data.items || [])
                setError(null)
            } else {
                setError(response.data?.message || 'Could not load this bulk send.')
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Could not load this bulk send.')
        } finally {
            inFlight.current = false
            setLoaded(true)
        }
    }, [batchId])

    useEffect(() => {
        setBatch(null)
        setItems([])
        setLoaded(false)
        setError(null)
        if (batchId) refresh()
    }, [batchId, refresh])

    const running = batch?.status === 'running'
    useEffect(() => {
        if (!batchId || !running) return
        const id = window.setInterval(refresh, DETAIL_POLL_MS)
        return () => window.clearInterval(id)
    }, [batchId, running, refresh])

    return { batch, items, loaded, error, refresh }
}
